import { useAuth } from "@dl/web-core-js/auth";
import { Card, Select, SeriesBarChart, TabNotPorted } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { billedOf, figuresFor } from "../../api/figures";
import {
  ReportingClient,
  type InvoicePartAggregateDTO,
  type Period,
} from "../../api/reporting";
import type { ProductDTO } from "../../api/product";

const YEARS_BACK = 5;

type Mode = "money" | "units";

/**
 * Five years of this SKU's sales: a CAD/USD chart and a per-client breakdown.
 *
 * The Money/Units toggle exists because the two answer different questions —
 * "what is this product worth" and "how fast does it move" — and a SKU can rank
 * high on one and low on the other, especially across a currency split.
 */
export function SalesTab({ draft }: { draft: ProductDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ReportingClient(http), [http]);
  const [mode, setMode] = useState<Mode>("money");

  const sku = draft.metadata?.sku ?? "";

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: YEARS_BACK }, (_, index) => current - (YEARS_BACK - 1) + index);
  }, []);

  const periodOf = (year: number): Period => ({ from: `${year}-01-01`, to: `${year}-12-31` });

  // Ten small aggregate calls (5 years × 2 currencies), fired together — the
  // same shape the Vaadin chart used; each is a cheap pre-aggregated query.
  const chartQuery = useQuery({
    queryKey: ["product", "sales-chart", sku],
    queryFn: async () => {
      const rows = await Promise.all(
        years.map(async (year) => {
          const [cad, usd] = await Promise.all([
            client.invoicedForPartCurrency(sku, "CAD", periodOf(year)),
            client.invoicedForPartCurrency(sku, "USD", periodOf(year)),
          ]);
          return {
            year: String(year),
            // Billed, pinned. There is no basis control on the product
            // editor, and a figure that silently follows a toggle set two
            // screens away is worse than one that states its own terms.
            cad_money: figuresFor(cad, "all").billed,
            usd_money: figuresFor(usd, "all").billed,
            cad_units: figuresFor(cad, "all").units_billed,
            usd_units: figuresFor(usd, "all").units_billed,
          };
        }),
      );
      return rows;
    },
    enabled: sku !== "",
    staleTime: 5 * 60_000,
  });

  // Per-client rows, one call per year, keyed by client id for the pivot.
  const gridQuery = useQuery({
    queryKey: ["product", "sales-clients", sku],
    queryFn: async () => {
      const byYear = await Promise.all(
        years.map(async (year) => ({
          year,
          rows: await client.partRowsByClient(sku, periodOf(year)),
        })),
      );

      const clients = new Map<number, { name: string; currency: string }>();
      const cells = new Map<string, InvoicePartAggregateDTO>();
      for (const { year, rows } of byYear) {
        for (const row of rows) {
          const id = row.client?.cli_id;
          if (id === undefined) continue;
          clients.set(id, {
            name: row.client?.cli_name1 ?? `#${id}`,
            currency: row.client?.cli_currency ?? row.currency ?? "",
          });
          cells.set(`${id}:${year}`, row);
        }
      }
      return { clients, cells };
    },
    enabled: sku !== "",
    staleTime: 5 * 60_000,
  });

  if (sku === "") {
    return <TabNotPorted message={t("product.sales.no-sku", "This product has no SKU yet.")} />;
  }

  const money = mode === "money";
  const currentYear = years[years.length - 1] as number;

  const clientRows = [...(gridQuery.data?.clients.entries() ?? [])].sort((a, b) => {
    // Current-year value descending — the sort the Vaadin grid defaulted to.
    const valueOf = (id: number) => {
      const cell = gridQuery.data?.cells.get(`${id}:${currentYear}`);
      return money ? billedOf(cell?.totals) : (cell?.totals?.quantity ?? 0);
    };
    return valueOf(b[0]) - valueOf(a[0]);
  });

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="mb-3 flex items-center gap-3">
          <h3 className="text-[13px] font-semibold text-ink">
            {t("product.sales.title", "{{sku}} sales", { sku })}
          </h3>
          <Select
            value={mode}
            options={[
              { value: "money", label: t("product.sales.money", "Money") },
              { value: "units", label: t("product.sales.units", "Units") },
            ]}
            onChange={(event) => setMode(event.target.value as Mode)}
            className="ml-auto w-32"
          />
        </div>

        {chartQuery.isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        ) : (
          <SeriesBarChart
            data={chartQuery.data ?? []}
            xKey="year"
            series={[
              { key: money ? "cad_money" : "cad_units", label: "CAD", color: "var(--chart-1)" },
              { key: money ? "usd_money" : "usd_units", label: "USD", color: "var(--chart-2)" },
            ]}
            formatValue={(value) =>
              money
                ? new Intl.NumberFormat(undefined, { notation: "compact" }).format(value)
                : new Intl.NumberFormat().format(value)
            }
          />
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-line bg-surface-sunk text-[11px] uppercase tracking-wider text-ink-faint">
              <th className="px-4 py-2 text-left">{t("column.client", "Client")}</th>
              {years.map((year) => (
                <th key={year} className="px-4 py-2 text-right">
                  {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gridQuery.isLoading ? (
              <tr>
                <td colSpan={years.length + 1} className="px-4 py-8 text-center text-ink-faint">
                  …
                </td>
              </tr>
            ) : gridQuery.isError ? (
              <tr>
                <td colSpan={years.length + 1} className="px-4 py-8 text-center text-risk">
                  {t("error.generic", "Something went wrong. Please try again.")}
                </td>
              </tr>
            ) : clientRows.length === 0 ? (
              <tr>
                <td colSpan={years.length + 1} className="px-4 py-8 text-center text-ink-faint">
                  {t("product.sales.no-clients", "No invoiced sales in the last {{years}} years.", {
                    years: YEARS_BACK,
                  })}
                </td>
              </tr>
            ) : (
              clientRows.map(([id, meta]) => (
                <tr key={id} className="border-b border-line-soft last:border-0">
                  <td className="px-4 py-2">
                    <span className="block font-medium text-ink">{meta.name}</span>
                    <span className="block text-[12px] text-ink-muted">{meta.currency}</span>
                  </td>
                  {years.map((year) => {
                    const cell = gridQuery.data?.cells.get(`${id}:${year}`);
                    if (!cell?.totals) {
                      return (
                        <td key={year} className="tabular px-4 py-2 text-right text-ink-faint">
                          
                        </td>
                      );
                    }
                    return (
                      <td key={year} className="tabular px-4 py-2 text-right">
                        <span className="block text-ink">
                          {new Intl.NumberFormat(undefined, {
                            maximumFractionDigits: 0,
                          }).format(billedOf(cell.totals))}
                        </span>
                        <span className="block text-[12px] text-ink-muted">
                          {t("product.sales.units-cell", "{{count}} units", {
                            count: cell.totals.quantity,
                          })}
                          {cell.totals.items !== cell.totals.quantity
                            ? ` · ${t("product.sales.direct-cell", "{{count}} direct", { count: cell.totals.items })}`
                            : ""}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
